import React, { useState } from 'react';
import { Table, Button, Form, Badge, InputGroup, Dropdown, DropdownButton } from 'react-bootstrap';
import { FaFileAlt, FaFileInvoiceDollar, FaExchangeAlt, FaReceipt, FaFolder, FaDownload, FaEnvelope, FaTrash, FaImage, FaSearch, FaSortAmountDown, FaSortAmountUp, FaFilter } from 'react-icons/fa';
import { downloadDocument } from '../../utils/documentUtils';
import { toast } from 'react-toastify';

const DocumentsTableView = ({ documents, receipts, token, onDelete, clientEmail }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Categorize documents
  const categorizeDocument = (doc) => {
    const name = doc.DocumentName?.toLowerCase() || '';
    const type = doc.DocumentType?.toLowerCase() || '';
    
    if (name.includes('proposal') || type.includes('proposal')) return 'proposal';
    if (name.includes('invoice') || type.includes('invoice')) return 'invoice';
    if (name.includes('change') || name.includes('order') || type.includes('change')) return 'change_order';
    return 'other';
  };

  // Combine documents and receipts into unified list
  const getAllItems = () => {
    const docItems = documents.map(doc => ({
      id: doc.DocumentId,
      name: doc.DocumentName,
      type: categorizeDocument(doc),
      date: new Date(doc.CreatedAt),
      itemType: 'document',
      original: doc
    }));

    const receiptItems = receipts.map(r => ({
      id: r.ScanId,
      name: r.StoreName || 'Unknown Store',
      type: 'receipt',
      date: new Date(r.ScannedAt),
      amount: parseFloat(r.TotalAmount || 0),
      status: r.Status,
      itemCount: r.ItemCount,
      itemType: 'receipt',
      original: r
    }));

    return [...docItems, ...receiptItems];
  };

  // Filter and sort items
  const getFilteredAndSortedItems = () => {
    let items = getAllItems();

    // Apply type filter
    if (filterType !== 'all') {
      items = items.filter(item => item.type === filterType);
    }

    // Apply search
    if (searchTerm) {
      items = items.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sort
    items.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'date') {
        comparison = a.date - b.date;
      } else if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortField === 'type') {
        comparison = a.type.localeCompare(b.type);
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return items;
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'proposal': return <FaFileAlt className="text-primary" />;
      case 'invoice': return <FaFileInvoiceDollar className="text-success" />;
      case 'change_order': return <FaExchangeAlt className="text-warning" />;
      case 'receipt': return <FaReceipt className="text-info" />;
      default: return <FaFolder className="text-secondary" />;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      proposal: 'primary',
      invoice: 'success',
      change_order: 'warning',
      receipt: 'info',
      other: 'secondary'
    };
    const labels = {
      proposal: 'Proposal',
      invoice: 'Invoice',
      change_order: 'Change Order',
      receipt: 'Receipt',
      other: 'Other'
    };
    return <Badge bg={colors[type] || 'secondary'}>{labels[type] || type}</Badge>;
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <FaSortAmountUp className="ms-1" /> : <FaSortAmountDown className="ms-1" />;
  };

  const items = getFilteredAndSortedItems();

  return (
    <div>
      {/* Search and Filter Bar */}
      <div className="d-flex flex-wrap gap-3 mb-4">
        <InputGroup style={{ maxWidth: '300px' }}>
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <DropdownButton
          variant="outline-secondary"
          title={
            <>
              <FaFilter className="me-1" />
              {filterType === 'all' ? 'All Types' : getTypeBadge(filterType)}
            </>
          }
        >
          <Dropdown.Item onClick={() => setFilterType('all')}>
            All Types
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => setFilterType('proposal')}>
            <FaFileAlt className="me-2 text-primary" /> Proposals
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setFilterType('invoice')}>
            <FaFileInvoiceDollar className="me-2 text-success" /> Invoices
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setFilterType('change_order')}>
            <FaExchangeAlt className="me-2 text-warning" /> Change Orders
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setFilterType('receipt')}>
            <FaReceipt className="me-2 text-info" /> Receipts
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setFilterType('other')}>
            <FaFolder className="me-2 text-secondary" /> Other
          </Dropdown.Item>
        </DropdownButton>

        <Badge bg="light" text="dark" className="d-flex align-items-center px-3">
          {items.length} item{items.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Documents Table */}
      <div className="table-responsive">
        <Table hover>
          <thead className="table-light">
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('type')}>
                Type <SortIcon field="type" />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                Name <SortIcon field="name" />
              </th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('date')}>
                Date <SortIcon field="date" />
              </th>
              <th>Details</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map(item => (
                <tr key={`${item.itemType}-${item.id}`}>
                  <td>
                    <div className="d-flex align-items-center">
                      {getTypeIcon(item.type)}
                      <span className="ms-2">{getTypeBadge(item.type)}</span>
                    </div>
                  </td>
                  <td>
                    <span className="fw-medium">{item.name}</span>
                  </td>
                  <td>
                    {item.date.toLocaleDateString()}
                  </td>
                  <td>
                    {item.itemType === 'receipt' ? (
                      <div className="small">
                        <span className="text-success fw-medium">${item.amount.toFixed(2)}</span>
                        <span className="text-muted mx-1">•</span>
                        <span>{item.itemCount} items</span>
                        <span className="text-muted mx-1">•</span>
                        <Badge 
                          bg={item.status === 'approved' ? 'success' : item.status === 'pending' ? 'warning' : 'secondary'}
                          className="ms-1"
                        >
                          {item.status}
                        </Badge>
                      </div>
                    ) : (
                      <span className="text-muted small">—</span>
                    )}
                  </td>
                  <td className="text-end">
                    {item.itemType === 'document' ? (
                      <>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => downloadDocument(item.id, item.name, token)}
                          title="Download"
                        >
                          <FaDownload />
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-1"
                          disabled={!clientEmail}
                          title={!clientEmail ? "No client email" : "Email to client"}
                        >
                          <FaEnvelope />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => onDelete(item.id)}
                          title="Delete"
                        >
                          <FaTrash />
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline-info"
                        size="sm"
                        title="View Receipt Image"
                      >
                        <FaImage />
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-5 text-muted">
                  <FaFolder size={32} className="mb-2" />
                  <p className="mb-0">No documents found</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default DocumentsTableView;
